import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { useSurveyImport } from '../../../hooks/useSurveyImport';
import { generateImportPreview, generateTablePreview } from '../../../lib/surveyPreview';
import { validateCompleteImport } from '../../../lib/surveyValidation';
import { fetchAllClients, fetchAllPrograms, fetchSOsByClientAndProgram } from '../../../lib/airtable';
import { TypeaheadSelect } from '../../ui/TypeaheadSelect';
import { ExternalSurveyHistory } from './ExternalSurveyHistory';

/**
 * External Survey Import Component
 * Provides drag-and-drop Excel/CSV import functionality for the Admin Dashboard
 */
export default function ExternalSurveyImport() {
  const [viewMode, setViewMode] = useState('import'); // 'import' | 'history'
  const [client, setClient] = useState('');
  const [program, setProgram] = useState('');
  const [associatedSOs, setAssociatedSOs] = useState('');
  const [availableSOs, setAvailableSOs] = useState([]);
  const [selectedSOs, setSelectedSOs] = useState([]);
  const [isLoadingSOs, setIsLoadingSOs] = useState(false);
  const [importOptions, setImportOptions] = useState({
    surveyTitle: '',
    sourceType: 'Client Excel',
    notes: '',
    importedBy: 'Admin'
  });

  // Dropdown data
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [previewMode, setPreviewMode] = useState('summary'); // 'summary' | 'table' | 'questions'
  const fileInputRef = useRef(null);

  // Filter programs based on selected client
  const filteredPrograms = React.useMemo(() => {
    if (!client || !programs.length) {
      return programs;
    }
    
    // Find the selected client
    const selectedClient = clients.find(c => c.name === client);
    if (!selectedClient) {
      return programs;
    }
    
    // Filter programs that are linked to this client
    return programs.filter(program => 
      program.clientIds.includes(selectedClient.id)
    );
  }, [client, programs, clients]);

  // Handle client selection change - clear program if it's not available for the new client
  const handleClientChange = useCallback((newClient) => {
    setClient(newClient);
    
    // Clear SOs when client changes
    setAvailableSOs([]);
    setSelectedSOs([]);
    
    // If a program is selected, check if it's still valid for the new client
    if (program && newClient) {
      const selectedClientObj = clients.find(c => c.name === newClient);
      if (selectedClientObj) {
        const selectedProgramObj = programs.find(p => p.name === program);
        if (selectedProgramObj && !selectedProgramObj.clientIds.includes(selectedClientObj.id)) {
          // Clear program since it's not available for the new client
          setProgram('');
        }
      }
    }
  }, [program, clients, programs]);

  // Handle SO selection
  const handleSOToggle = useCallback((soNumber) => {
    setSelectedSOs(prev => {
      if (prev.includes(soNumber)) {
        return prev.filter(so => so !== soNumber);
      } else {
        return [...prev, soNumber];
      }
    });
  }, []);

  // Update associatedSOs when selectedSOs changes
  useEffect(() => {
    setAssociatedSOs(selectedSOs.join(', '));
  }, [selectedSOs]);

  // Fetch clients and programs for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setIsLoadingDropdowns(true);
        
        // Fetch clients and programs using existing API functions
        const [clientData, programData] = await Promise.all([
          fetchAllClients(),
          fetchAllPrograms()
        ]);

        // Data is already transformed by the API functions
        setClients(clientData);
        setPrograms(programData);
      } catch (error) {
        // Set empty arrays so dropdowns still render
        setClients([]);
        setPrograms([]);
        // Error is handled gracefully - dropdowns will show empty state
      } finally {
        setIsLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch SOs when client and program are selected
  useEffect(() => {
    const fetchSOs = async () => {
      if (!client || !program) {
        setAvailableSOs([]);
        setSelectedSOs([]);
        return;
      }

      try {
        setIsLoadingSOs(true);
        const soData = await fetchSOsByClientAndProgram(client, program);
        setAvailableSOs(soData);
        setSelectedSOs([]); // Clear previous selections
      } catch (error) {
        console.error('Failed to fetch SOs:', error);
        setAvailableSOs([]);
        setSelectedSOs([]);
      } finally {
        setIsLoadingSOs(false);
      }
    };

    fetchSOs();
  }, [client, program]);

  const {
    file,
    isProcessing,
    parsedData,
    questionMapping,
    previewData,
    validationResults,
    memoryEstimate,
    isImporting,
    importProgress,
    importResults,
    error,
    warnings,
    processFile,
    validateForImport,
    executeImport,
    reset,
    cancelOperation,
    isFileTypeSupported,
    getFileValidationMessage,
    SUPPORTED_FILE_TYPES
  } = useSurveyImport();

  // File handling
  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    const validationMessage = getFileValidationMessage(selectedFile);
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    const success = await processFile(selectedFile, { client, program, associatedSOs });
    if (success) {
      // Auto-set survey title from filename if not provided
      if (!importOptions.surveyTitle) {
        const baseName = selectedFile.name.replace(/\.[^/.]+$/, '');
        setImportOptions(prev => ({
          ...prev,
          surveyTitle: baseName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }));
      }
    }
  }, [processFile, client, program, associatedSOs, getFileValidationMessage, importOptions.surveyTitle]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // Validation and import
  const handleValidate = useCallback(async () => {
    if (!client || !program) {
      alert('Please enter both client and program');
      return;
    }

    const isValid = validateForImport({ client, program, associatedSOs }, importOptions);
    // Note: Removed redundant comprehensive validation that was too strict for survey data
    // The validateForImport hook already provides appropriate validation for real-world survey files
  }, [client, program, associatedSOs, validateForImport, importOptions, file, parsedData, questionMapping, previewData]);

  const handleImport = useCallback(async () => {
    if (!validationResults?.isValid) {
      alert('Please validate the data before importing');
      return;
    }

    await executeImport({ client, program, associatedSOs }, importOptions);
  }, [validationResults, executeImport, client, program, associatedSOs, importOptions]);

  const handleReset = useCallback(() => {
    reset();
    setClient('');
    setProgram('');
    setAssociatedSOs('');
    setAvailableSOs([]);
    setSelectedSOs([]);
    setImportOptions({
      surveyTitle: '',
      sourceType: 'Client Excel',
      notes: '',
      importedBy: 'Admin'
    });
    setPreviewMode('summary');
  }, [reset]);

  // Generate enhanced preview data
  const enhancedPreview = previewData && parsedData && questionMapping 
    ? generateImportPreview({
        importRecord: {
          Client: client,
          Program: program,
          'Associated SOs': associatedSOs || 'None',
          'Survey Title': importOptions.surveyTitle,
          'Source Type': importOptions.sourceType,
          'Original Filename': file?.name || '',
          'Import Date': new Date().toISOString().split('T')[0],
          'Response Count': previewData.responses?.length || 0,
          'Question Mapping': JSON.stringify(questionMapping),
          'Import Notes': importOptions.notes,
          'Imported By': importOptions.importedBy
        },
        responses: previewData.responses || [],
        summary: previewData.summary || {}
      })
    : null;

  const tablePreview = enhancedPreview && questionMapping
    ? generateTablePreview({
        importRecord: enhancedPreview.importInfo,
        responses: previewData.responses || [],
        questionMapping: questionMapping
      })
    : null;

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle>External Survey Import</CardTitle>
          <CardDescription>
            Import survey data from Excel or CSV files for non-Typeform surveys
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setViewMode('import')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              viewMode === 'import'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Import New Survey
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              viewMode === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Import History
          </button>
        </nav>
      </div>

      {/* Conditional Content */}
      {viewMode === 'history' ? (
        <ExternalSurveyHistory />
      ) : (
        <div className="space-y-6">

      {/* Import Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Import Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <TypeaheadSelect
                  label="Client"
                  required
                  options={clients}
                  value={client}
                  onChange={handleClientChange}
                  placeholder={isLoadingDropdowns ? 'Loading clients...' : 'Type to search clients...'}
                  disabled={isProcessing || isImporting || isLoadingDropdowns}
                />
              </div>
              <div>
                <TypeaheadSelect
                  label="Program"
                  required
                  options={filteredPrograms}
                  value={program}
                  onChange={setProgram}
                  placeholder={isLoadingDropdowns ? 'Loading programs...' : (client ? 'Type to search programs for this client...' : 'Select a client first...')}
                  disabled={isProcessing || isImporting || isLoadingDropdowns}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Associated SOs (Optional)</label>
              {(!client || !program) ? (
                <div className="p-3 bg-gray-50 border rounded-md">
                  <p className="text-sm text-gray-500">Select client and program first to see available SOs</p>
                </div>
              ) : isLoadingSOs ? (
                <div className="p-3 bg-gray-50 border rounded-md">
                  <p className="text-sm text-gray-500">Loading SOs...</p>
                </div>
              ) : availableSOs.length === 0 ? (
                <div className="p-3 bg-gray-50 border rounded-md">
                  <p className="text-sm text-gray-500">No SOs found for this client and program</p>
                </div>
              ) : (
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
                  <div className="space-y-2">
                    {availableSOs.map((so) => (
                      <label key={so.so} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSOs.includes(so.so)}
                          onChange={() => handleSOToggle(so.so)}
                          disabled={isProcessing || isImporting}
                          className="rounded"
                        />
                        <span className="text-sm">
                          <strong>SO {so.so}</strong> - {so.type} {so.language}
                          {so.deliveryStart && (
                            <span className="text-gray-500"> ({new Date(so.deliveryStart).toLocaleDateString()})</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select specific SOs to associate this survey with, or leave none selected for client/program association only
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Survey Title</label>
              <input
                type="text"
                value={importOptions.surveyTitle}
                onChange={(e) => setImportOptions(prev => ({ ...prev, surveyTitle: e.target.value }))}
                className="w-full p-2 border rounded-md"
                placeholder="e.g., Exit Survey, Client Feedback"
                disabled={isProcessing || isImporting}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Source Type</label>
              <select
                value={importOptions.sourceType}
                onChange={(e) => setImportOptions(prev => ({ ...prev, sourceType: e.target.value }))}
                className="w-full p-2 border rounded-md"
                disabled={isProcessing || isImporting}
              >
                <option value="Client Excel">Client Excel</option>
                <option value="Client CSV">Client CSV</option>
                <option value="Legacy Database">Legacy Database</option>
                <option value="Manual Entry">Manual Entry</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Imported By</label>
              <input
                type="text"
                value={importOptions.importedBy}
                onChange={(e) => setImportOptions(prev => ({ ...prev, importedBy: e.target.value }))}
                className="w-full p-2 border rounded-md"
                placeholder="Your name or email"
                disabled={isProcessing || isImporting}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              value={importOptions.notes}
              onChange={(e) => setImportOptions(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full p-2 border rounded-md"
              rows={3}
              placeholder="Any additional context about this import..."
              disabled={isProcessing || isImporting}
            />
          </div>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle>File Upload</CardTitle>
          <CardDescription>
            Drag and drop your Excel or CSV file, or click to browse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : file 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={SUPPORTED_FILE_TYPES.ALL.map(ext => `.${ext}`).join(',')}
              onChange={(e) => handleFileSelect(e.target.files[0])}
              disabled={isProcessing || isImporting}
            />
            
            {isProcessing ? (
              <div className="space-y-2">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-lg font-medium">Processing file...</p>
              </div>
            ) : file ? (
              <div className="space-y-2">
                <div className="text-green-600 text-2xl">✓</div>
                <p className="text-lg font-medium">{file.name}</p>
                <p className="text-sm text-gray-600">
                  {Math.round(file.size / 1024)} KB • {file.type || getFileExtension(file.name).toUpperCase()}
                </p>
                {previewData && (
                  <p className="text-sm text-green-600">
                    {previewData.summary?.total_responses || 0} responses • {previewData.summary?.questions_count || 0} questions detected
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-gray-400 text-2xl">📁</div>
                <p className="text-lg font-medium">Drop your file here or click to browse</p>
                <p className="text-sm text-gray-600">
                  Supports: {SUPPORTED_FILE_TYPES.ALL.join(', ').toUpperCase()} • Max 10MB
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="text-yellow-800 text-sm">
                <p className="font-medium">Warnings:</p>
                <ul className="list-disc list-inside mt-1">
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview and Validation */}
      {previewData && (
        <Card>
          <CardHeader>
            <CardTitle>Data Preview</CardTitle>
            <CardDescription>
              Review your data before import
            </CardDescription>
            <div className="flex space-x-2 mt-4">
              <button
                onClick={() => setPreviewMode('summary')}
                className={`px-3 py-1 rounded text-sm ${previewMode === 'summary' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                Summary
              </button>
              <button
                onClick={() => setPreviewMode('table')}
                className={`px-3 py-1 rounded text-sm ${previewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                Data Table
              </button>
              <button
                onClick={() => setPreviewMode('questions')}
                className={`px-3 py-1 rounded text-sm ${previewMode === 'questions' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                Questions
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {previewMode === 'summary' && enhancedPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {enhancedPreview.statisticalSummary.responses.total}
                    </p>
                    <p className="text-sm text-blue-800">Total Responses</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {enhancedPreview.statisticalSummary.responses.completionRate}%
                    </p>
                    <p className="text-sm text-green-800">Completion Rate</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {enhancedPreview.questionsOverview.total}
                    </p>
                    <p className="text-sm text-purple-800">Questions</p>
                  </div>
                </div>

                {enhancedPreview.dataQualityAnalysis && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Data Quality</h4>
                    <div className="flex items-center space-x-4">
                      <div className={`text-3xl font-bold ${
                        enhancedPreview.dataQualityAnalysis.overall.grade === 'A' ? 'text-green-600' :
                        enhancedPreview.dataQualityAnalysis.overall.grade === 'B' ? 'text-blue-600' :
                        enhancedPreview.dataQualityAnalysis.overall.grade === 'C' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {enhancedPreview.dataQualityAnalysis.overall.grade}
                      </div>
                      <div>
                        <p className="font-medium">{enhancedPreview.dataQualityAnalysis.overall.score}% Quality Score</p>
                        <p className="text-sm text-gray-600">{enhancedPreview.dataQualityAnalysis.overall.description}</p>
                      </div>
                    </div>
                  </div>
                )}

                {enhancedPreview.recommendations.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">Recommendations</h4>
                    {enhancedPreview.recommendations.map((rec, index) => (
                      <div key={index} className={`p-3 rounded border-l-4 ${
                        rec.priority === 'high' ? 'border-red-500 bg-red-50' :
                        rec.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                        'border-blue-500 bg-blue-50'
                      }`}>
                        <p className="font-medium text-sm">{rec.title}</p>
                        <p className="text-xs text-gray-600">{rec.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {previewMode === 'table' && tablePreview && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-2 text-left">Row</th>
                      {tablePreview.columns.map(col => (
                        <th key={col.id} className={`border border-gray-300 p-2 text-left ${
                          col.width === 'wide' ? 'min-w-48' : col.width === 'narrow' ? 'w-20' : 'min-w-32'
                        }`}>
                          <div>
                            <div className="font-medium">{col.title}</div>
                            <div className="text-xs text-gray-500">{col.type}</div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tablePreview.rows.map(row => (
                      <tr key={row.id} className={row.quality === 'invalid' ? 'bg-red-50' : ''}>
                        <td className="border border-gray-300 p-2 text-center font-mono text-xs">
                          {row.originalRow}
                        </td>
                        {tablePreview.columns.map(col => (
                          <td key={col.id} className="border border-gray-300 p-2">
                            <div className={row.cells[col.id]?.hasWarning ? 'text-yellow-600' : ''}>
                              {row.cells[col.id]?.formatted || '(empty)'}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {tablePreview.hasMoreRows && (
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {tablePreview.rows.length} of {tablePreview.totalRows} rows
                  </p>
                )}
              </div>
            )}

            {previewMode === 'questions' && questionMapping && (
              <div className="space-y-3">
                {questionMapping.questions.map((question, index) => (
                  <div key={question.id} className="border rounded p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {question.column}
                          </span>
                        </div>
                        <p className="font-medium">{question.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {file && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleValidate}
                disabled={!client || !program || isProcessing || isImporting}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
              >
                Validate Data
              </button>
              
              <button
                onClick={handleImport}
                disabled={!validationResults?.isValid || isImporting}
                className="px-4 py-2 bg-green-500 text-white rounded disabled:bg-gray-300"
              >
                {isImporting ? `Importing... ${importProgress}%` : 'Import Survey Data'}
              </button>
              
              <button
                onClick={cancelOperation}
                disabled={!isProcessing && !isImporting}
                className="px-4 py-2 bg-red-500 text-white rounded disabled:bg-gray-300"
              >
                Cancel
              </button>
              
              <button
                onClick={handleReset}
                disabled={isProcessing || isImporting}
                className="px-4 py-2 bg-gray-500 text-white rounded disabled:bg-gray-300"
              >
                Reset
              </button>
            </div>

            {validationResults && (
              <div className="mt-4 p-3 border rounded">
                <p className={`font-medium ${validationResults.isValid ? 'text-green-600' : 'text-red-600'}`}>
                  {validationResults.isValid ? '✓ Validation Passed' : '✗ Validation Failed'}
                </p>
                {validationResults.warnings?.length > 0 && (
                  <div className="mt-2 text-sm text-yellow-600">
                    <p>Warnings:</p>
                    <ul className="list-disc list-inside">
                      {validationResults.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {importResults && (
              <div className="mt-4 p-3 border rounded">
                {importResults.success ? (
                  <div className="text-green-600">
                    <p className="font-medium">✓ Import Successful!</p>
                    <p className="text-sm">
                      Created {importResults.responsesCreated} response records for {importResults.client} - {importResults.program}
                    </p>
                    <button
                      onClick={() => setViewMode('history')}
                      className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      View Import History
                    </button>
                  </div>
                ) : (
                  <div className="text-red-600">
                    <p className="font-medium">✗ Import Failed</p>
                    <p className="text-sm">{importResults.error}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Memory Usage Info */}
      {memoryEstimate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Memory Usage Estimate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium">Total Size</p>
                <p>{memoryEstimate.total_size.mb} MB</p>
              </div>
              <div>
                <p className="font-medium">Airtable Fields</p>
                <p>{memoryEstimate.airtable_fields_needed}</p>
              </div>
              <div>
                <p className="font-medium">Responses/Field</p>
                <p>{memoryEstimate.responses_per_field}</p>
              </div>
              <div>
                <p className="font-medium">Est. Import Time</p>
                <p>{previewData ? calculateImportTime(previewData.summary?.total_responses || 0) : 'Unknown'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
        </div>
      )}
    </div>
  );
}

// Helper functions
function getFileExtension(filename) {
  return filename.split('.').pop().toLowerCase();
}

function calculateImportTime(responseCount) {
  const seconds = Math.ceil(responseCount / 10); // Rough estimate
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
  return `${Math.ceil(seconds / 3600)}h`;
}